import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, Ban, AlertTriangle, Shield, Scale } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Footer from '../components/Footer';

const AcceptableUsePage = () => {
  const { isAuthenticated } = useAuth();
  const homeRoute = isAuthenticated ? '/dashboard' : '/';
  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to={homeRoute} className="flex items-center text-gray-600 hover:text-gray-800">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Link>
          <Link to={homeRoute} className="flex items-center space-x-2">
            <Sparkles className="w-6 h-6 text-rose-400" />
            <span className="text-xl font-bold bg-gradient-to-r from-rose-500 to-orange-500 bg-clip-text text-transparent">VirtualFit</span>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12" data-testid="acceptable-use-page">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Acceptable Use Policy</h1>
        <p className="text-gray-500 mb-8">Last updated: March 12, 2026</p>

        <div className="prose prose-gray max-w-none space-y-8">
          <section>
            <p className="text-gray-600">
              This Acceptable Use Policy ("AUP") governs your use of the VirtualFit platform and its AI-powered virtual try-on features. By using VirtualFit, you agree to comply with this policy. Violation may result in immediate account suspension or termination.
            </p>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-3">
              <Ban className="w-5 h-5 text-red-500" />
              <h2 className="text-xl font-semibold text-gray-900 m-0">Prohibited Uses</h2>
            </div>
            <p className="text-gray-600 mb-3">You may <strong>not</strong> use VirtualFit to:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>Create deepfakes</strong> — Generate images that misrepresent a person's identity, place someone in a false context, or create non-consensual intimate imagery.</li>
              <li><strong>Harass or bully</strong> — Upload photos of others without their consent for the purpose of mockery, harassment, or intimidation.</li>
              <li><strong>Upload photos of minors</strong> — Users must be at least 18 years old. Do not upload photos of individuals under 18.</li>
              <li><strong>Infringe intellectual property</strong> — Upload copyrighted images, brand logos, or proprietary designs without authorization.</li>
              <li><strong>Deceive consumers</strong> — Use generated images to mislead customers about product appearance, fit, or quality in commercial contexts.</li>
              <li><strong>Generate explicit content</strong> — Create, distribute, or share sexually explicit, violent, or otherwise harmful imagery.</li>
              <li><strong>Commit fraud</strong> — Use generated images for identity theft, insurance fraud, counterfeit product listings, or any fraudulent activity.</li>
              <li><strong>Reverse engineer</strong> — Attempt to extract, copy, or reverse-engineer VirtualFit's AI models, algorithms, or proprietary technology.</li>
              <li><strong>Automate or scrape</strong> — Use bots, scripts, or automated tools to access VirtualFit's services without written permission.</li>
            </ul>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-5 h-5 text-green-500" />
              <h2 className="text-xl font-semibold text-gray-900 m-0">Acceptable Uses</h2>
            </div>
            <p className="text-gray-600 mb-3">VirtualFit is designed for:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Trying on clothing virtually using your own photo</li>
              <li>Brands showcasing their collections on model representations</li>
              <li>Influencers creating look-books and collaboration content</li>
              <li>Personal shoppers visualizing outfits for clients (with client consent)</li>
              <li>E-commerce product visualization and marketing</li>
            </ul>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              <h2 className="text-xl font-semibold text-gray-900 m-0">AI-Generated Content Disclaimer</h2>
            </div>
            <p className="text-gray-600">
              Images generated by VirtualFit are <strong>AI-created approximations</strong> and may not perfectly represent how a garment will look in real life. Factors such as fabric texture, color accuracy, fit, and drape may differ from the AI-generated output. VirtualFit does not guarantee the accuracy of generated images for purchasing decisions.
            </p>
            <p className="text-gray-600 mt-3">
              Users who share AI-generated images publicly should disclose that they are AI-generated. Misrepresenting AI-generated content as real photographs is a violation of this policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Content You Upload</h2>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>You must have the right to upload any photo you submit (your own photo, or with the subject's explicit consent).</li>
              <li>You retain ownership of photos you upload. By uploading, you grant VirtualFit a limited license to process the image for the purpose of providing the try-on service.</li>
              <li>VirtualFit reserves the right to remove content that violates this policy without notice.</li>
            </ul>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-3">
              <Scale className="w-5 h-5 text-rose-500" />
              <h2 className="text-xl font-semibold text-gray-900 m-0">Enforcement</h2>
            </div>
            <p className="text-gray-600">Violations of this policy may result in:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-1">
              <li>Warning and content removal</li>
              <li>Temporary account suspension</li>
              <li>Permanent account termination without refund</li>
              <li>Reporting to law enforcement authorities where applicable</li>
            </ul>
            <p className="text-gray-600 mt-3">
              VirtualFit reserves sole discretion in determining what constitutes a violation and what action to take.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Reporting Violations</h2>
            <p className="text-gray-600">
              If you encounter content or behavior that violates this policy, please report it to <a href="mailto:hello@virtualfit.work" className="text-rose-500 hover:underline">hello@virtualfit.work</a> with a description and any relevant screenshots or links.
            </p>
          </section>

          <section className="bg-gray-50 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Questions?</h2>
            <p className="text-gray-600">
              Contact us at <a href="mailto:hello@virtualfit.work" className="text-rose-500 hover:underline">hello@virtualfit.work</a> or visit our <Link to="/contact" className="text-rose-500 hover:underline">Contact</Link> page.
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AcceptableUsePage;
